import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings2, Ban, Plus, Trash2, Clock, CalendarRange } from "lucide-react";
import { format, eachDayOfInterval, parseISO } from "date-fns";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface BookingHour {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface BlockedDate {
  id: string;
  blocked_date: string;
  all_day: boolean;
  start_time: string | null;
  end_time: string | null;
  reason: string;
}

interface Props {
  selectedDate: Date | null;
  blockedDates: BlockedDate[];
  onBlockedDatesChange: () => void;
}

export const useAvailabilityData = () => {
  const [bookingHours, setBookingHours] = useState<BookingHour[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    const [hoursRes, blockedRes] = await Promise.all([
      supabase.from("booking_hours").select("*").order("day_of_week"),
      supabase.from("blocked_dates").select("*").order("blocked_date"),
    ]);
    setBookingHours((hoursRes.data as BookingHour[]) || []);
    setBlockedDates((blockedRes.data as BlockedDate[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  return { bookingHours, blockedDates, loading, refetch: fetchAll };
};

export const AvailabilityHoursEditor = ({
  bookingHours,
  onSave,
}: {
  bookingHours: BookingHour[];
  onSave: () => void;
}) => {
  const [hours, setHours] = useState<BookingHour[]>([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setHours(bookingHours);
  }, [bookingHours]);

  const updateHour = (dayOfWeek: number, field: string, value: any) => {
    setHours((prev) =>
      prev.map((h) => (h.day_of_week === dayOfWeek ? { ...h, [field]: value } : h))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    for (const h of hours) {
      await supabase
        .from("booking_hours")
        .update({
          start_time: h.start_time,
          end_time: h.end_time,
          is_available: h.is_available,
        })
        .eq("id", h.id);
    }
    toast.success("Booking hours updated");
    setSaving(false);
    onSave();
    setOpen(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setOpen(true)}>
        <Settings2 size={13} /> Hours
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock size={18} /> Booking Hours
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {hours.map((h) => (
              <div
                key={h.day_of_week}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  h.is_available ? "border-border/50 bg-background" : "border-border/20 bg-muted/30 opacity-60"
                }`}
              >
                <Switch
                  checked={h.is_available}
                  onCheckedChange={(v) => updateHour(h.day_of_week, "is_available", v)}
                />
                <span className="text-sm font-medium w-24">{DAY_NAMES[h.day_of_week]}</span>
                {h.is_available && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={h.start_time?.slice(0, 5)}
                      onChange={(e) => updateHour(h.day_of_week, "start_time", e.target.value)}
                      className="h-8 text-xs w-auto"
                    />
                    <span className="text-xs text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={h.end_time?.slice(0, 5)}
                      onChange={(e) => updateHour(h.day_of_week, "end_time", e.target.value)}
                      className="h-8 text-xs w-auto"
                    />
                  </div>
                )}
                {!h.is_available && (
                  <span className="text-xs text-muted-foreground italic">Closed</span>
                )}
              </div>
            ))}
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full mt-2">
            {saving ? "Saving…" : "Save Hours"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const BlockDateDialog = ({
  selectedDate,
  onSave,
  open,
  onOpenChange,
}: {
  selectedDate: Date | null;
  onSave: () => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const [blockDate, setBlockDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isRange, setIsRange] = useState(false);
  const [allDay, setAllDay] = useState(true);
  const [timeSlots, setTimeSlots] = useState([{ start: "", end: "" }]);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<
    Array<{ id: string; booking_date: string; booking_time: string; service_name: string; name: string }>
  >([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingInserts, setPendingInserts] = useState<any[] | null>(null);

  useEffect(() => {
    if (selectedDate) {
      const ds = format(selectedDate, "yyyy-MM-dd");
      setBlockDate(ds);
      setEndDate(ds);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (open) {
      setAllDay(true);
      setIsRange(false);
      setTimeSlots([{ start: "", end: "" }]);
      setConflicts([]);
      setPendingInserts(null);
      if (selectedDate) setEndDate(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [open, selectedDate]);


  const addTimeSlot = () => {
    setTimeSlots([...timeSlots, { start: "", end: "" }]);
  };

  // Partial blocks require an explicit, valid time range — no defaults are
  // pre-filled, so an admin can never save a partial block by accident.
  const slotsComplete =
    timeSlots.length > 0 &&
    timeSlots.every((s) => s.start && s.end && s.end > s.start);

  const removeTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const updateSlot = (index: number, field: "start" | "end", value: string) => {
    setTimeSlots(timeSlots.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleBlock = async () => {
    if (!blockDate) return;
    if (!allDay && !slotsComplete) {
      toast.error("Set a start and end time for every slot, or block the entire day");
      return;
    }
    setSaving(true);

    // Build the list of dates to block
    const startD = parseISO(blockDate);
    const endD = isRange && endDate ? parseISO(endDate) : startD;
    if (endD < startD) {
      toast.error("End date must be on or after start date");
      setSaving(false);
      return;
    }
    const dates = eachDayOfInterval({ start: startD, end: endD }).map((d) =>
      format(d, "yyyy-MM-dd")
    );

    let inserts: Array<{
      blocked_date: string;
      all_day: boolean;
      start_time: string | null;
      end_time: string | null;
      reason: string;
    }> = [];

    if (allDay) {
      inserts = dates.map((d) => ({
        blocked_date: d,
        all_day: true,
        start_time: null,
        end_time: null,
        reason: "",
      }));
    } else {
      // For each day, insert one row per time slot
      inserts = dates.flatMap((d) =>
        timeSlots.map((slot) => ({
          blocked_date: d,
          all_day: false,
          start_time: slot.start,
          end_time: slot.end,
          reason: "",
        }))
      );
    }

    // FAIL-SAFE: warn if clients already have appointments inside the window
    // being blocked — blocking does not cancel them, so they'd silently sit
    // on a "blocked" day.
    const { data: existing } = await supabase
      .from("bookings")
      .select("id, booking_date, booking_time, service_name, user_id").is("deleted_at", null)
      .in("booking_date", dates)
      .neq("status", "cancelled");

    const toMin = (t: string) => {
      const m = t?.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const p = m[3]?.toUpperCase();
      if (p === "PM" && h !== 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
      return h * 60 + parseInt(m[2], 10);
    };
    
    const hhmmToMin = (v: string) => {
      const [h, m] = v.split(":").map(Number);
      return h * 60 + (m || 0);
    };

    const clashing = (existing || []).filter((b) => {
      if (allDay) return true;
      const s = toMin(b.booking_time);
      if (s == null) return true;
      return timeSlots.some((slot) => s >= hhmmToMin(slot.start) && s < hhmmToMin(slot.end));
    });

    if (clashing.length > 0) {
      const userIds = Array.from(new Set(clashing.map((b) => b.user_id)));
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      const nameMap = new Map((profs || []).map((p: any) => [p.user_id, p.full_name || "Client"]));
      setConflicts(
        clashing.map((b) => ({
          id: b.id,
          booking_date: b.booking_date,
          booking_time: b.booking_time,
          service_name: b.service_name,
          name: nameMap.get(b.user_id) || "Client",
        })),
      );
      setPendingInserts(inserts);
      setConfirmOpen(true);
      setSaving(false);
      return;
    }

    await commitBlocks(inserts, dates.length);
  };

  const commitBlocks = async (inserts: any[], dayCount: number) => {
    setSaving(true);
    const { error } = await supabase.from("blocked_dates").insert(inserts);
    if (error) {
      toast.error("Failed to block");
      setSaving(false);
      return;
    }

    toast.success(
      allDay
        ? `${dayCount} day${dayCount !== 1 ? "s" : ""} blocked`
        : `${timeSlots.length} time slot(s) × ${dayCount} day${dayCount !== 1 ? "s" : ""} blocked`
    );
    setConfirmOpen(false);
    setPendingInserts(null);
    onSave();
    onOpenChange(false);
    setSaving(false);
  };


  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban size={18} /> Block Date / Time
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <Switch checked={allDay} onCheckedChange={setAllDay} />
            <span className="text-sm font-medium">
              Block entire day{isRange ? "s" : ""}
              <span className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground">standard</span>
            </span>
          </div>
          {!allDay && (
            <p className="text-xs text-destructive">
              Partial block — you must set a start and end time below. Everything outside those
              times stays bookable.
            </p>
          )}


          <div className="flex items-center gap-3">
            <Switch
              checked={isRange}
              onCheckedChange={(v) => {
                setIsRange(v);
                if (!v) setEndDate(blockDate);
              }}
            />
            <span className="text-sm flex items-center gap-1.5">
              <CalendarRange size={14} /> Block a date range (multiple days)
            </span>
          </div>


          <div className={isRange ? "grid grid-cols-2 gap-3" : ""}>
            <div>
              <label className="text-xs font-medium text-muted-foreground tracking-widest uppercase mb-1 block">
                {isRange ? "Start Date" : "Date"}
              </label>
              <Input
                type="date"
                value={blockDate}
                onChange={(e) => {
                  setBlockDate(e.target.value);
                  if (!isRange || !endDate || e.target.value > endDate) setEndDate(e.target.value);
                }}
                className="h-9 text-sm"
              />
            </div>
            {isRange && (
              <div>
                <label className="text-xs font-medium text-muted-foreground tracking-widest uppercase mb-1 block">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  min={blockDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            )}
          </div>

          {/* Time slots */}
          {!allDay && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground tracking-widest uppercase block">
                Time Slots {isRange && <span className="text-[10px] normal-case tracking-normal text-muted-foreground/70">(applied to every day in range)</span>}
              </label>
              {timeSlots.map((slot, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={slot.start}
                    onChange={(e) => updateSlot(i, "start", e.target.value)}
                    className="h-8 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={slot.end}
                    onChange={(e) => updateSlot(i, "end", e.target.value)}
                    className="h-8 text-xs"
                  />
                  {timeSlots.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10 shrink-0"
                      onClick={() => removeTimeSlot(i)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" className="text-xs gap-1.5 w-full" onClick={addTimeSlot}>
                <Plus size={13} /> Add Another Time Slot
              </Button>
            </div>
          )}

          <Button
            onClick={handleBlock}
            disabled={saving || !blockDate || (isRange && !endDate) || (!allDay && !slotsComplete)}
            className="w-full"
          >
            {saving ? "Checking…" : allDay ? (isRange ? "Block Days" : "Block Entire Day") : "Block Times"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      {/* Fail-safe: existing appointments inside the window being blocked */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>

        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Ban size={18} /> {conflicts.length} appointment{conflicts.length !== 1 ? "s" : ""} already booked
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Blocking will not cancel these appointments — they will stay on the schedule for a day you are
            marking unavailable. Reschedule or cancel them first if that isn't what you want.
          </p>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {conflicts.map((c) => (
              <div key={c.id} className="rounded-md border border-destructive/20 bg-destructive/5 p-2">
                <p className="text-sm font-medium">
                  {format(new Date(c.booking_date + "T00:00:00"), "EEE, MMM d")} · {c.booking_time}
                </p>
                <p className="text-xs text-muted-foreground">{c.name} — {c.service_name}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={saving}
              onClick={() =>
                pendingInserts &&
                commitBlocks(
                  pendingInserts,
                  new Set(pendingInserts.map((i: any) => i.blocked_date)).size,
                )
              }
            >
              {saving ? "Blocking…" : "Block anyway"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>

  );
};


export const BlockedDatesList = ({
  blockedDates,
  onDelete,
}: {
  blockedDates: BlockedDate[];
  onDelete: () => void;
}) => {
  const [open, setOpen] = useState(false);

  const handleDelete = async (id: string) => {
    await supabase.from("blocked_dates").delete().eq("id", id);
    toast.success("Block removed");
    onDelete();
  };

  const upcoming = blockedDates.filter(
    (b) => b.blocked_date >= format(new Date(), "yyyy-MM-dd")
  );

  if (upcoming.length === 0) return null;

  return (
    <>
      <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => setOpen(true)}>
        <Ban size={13} /> Blocked ({upcoming.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[60vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban size={18} /> Blocked Dates
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {upcoming.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between p-3 rounded-lg border border-destructive/20 bg-destructive/5"
              >
                <div>
                  <p className="text-sm font-medium">
                    {format(new Date(b.blocked_date + "T00:00:00"), "EEE, MMM d, yyyy")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {b.all_day
                      ? "All day"
                      : `${b.start_time?.slice(0, 5)} – ${b.end_time?.slice(0, 5)}`}
                    {b.reason ? ` · ${b.reason}` : ""}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(b.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
